import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';

import { axiosConfig } from '@/api/axiosConfig';
import { baseUrl, coreBaseUrl, zepBaseUrl } from '@/consts/env';

import { resolveAvatarColorsOrigin } from './useAvatarColors';

/**
 * 코어 아바타 카탈로그 응답.
 * 코어 `@zep/apis`의 `AvatarCatalog` 와 동일 구조 (service=quiz 카탈로그).
 */
export interface AvatarShapePart {
  hex: string | null;
  partId: number;
  imageVersion: number;
}

export interface AvatarShape {
  id: number;
  name: string;
  parts: AvatarShapePart[];
  previewPartId: number;
  isNew: boolean;
  isHot: boolean;
}

export interface AvatarCatalogCategory {
  id: number;
  name: string;
  slug: string;
  hasColor: boolean;
}

export interface AvatarCatalog {
  categories: AvatarCatalogCategory[];
  shapes: Record<string, AvatarShape[]>;
}

export type AvatarServiceKey = 'core' | 'quiz';

export const QUERY_KEY_AVATAR_CATALOG = 'avatar-catalog';

/**
 * 카탈로그/colors 모두 코어 .NET 서버의 `/api/avatar/*` 를 친다.
 * origin 결정은 colors 와 완전히 동일 (zepBaseUrl 우선).
 */
const AVATAR_CATALOG_INSTANCE = Axios.create({
  ...axiosConfig,
  baseURL: `${resolveAvatarColorsOrigin(
    coreBaseUrl,
    zepBaseUrl,
    baseUrl,
  )}/api/`,
});

export function useAvatarCatalog(
  service: AvatarServiceKey = 'quiz',
  options?: { enabled?: boolean },
) {
  return useQuery<AvatarCatalog>({
    queryKey: [QUERY_KEY_AVATAR_CATALOG, service],
    queryFn: async () => {
      const { data } = await AVATAR_CATALOG_INSTANCE.get('/avatar/catalog', {
        params: { service },
      });
      // 코어 응답은 { data: {...} } 또는 평면 객체 둘 다 올 수 있어 호환 처리.
      return data?.data ?? data;
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}
